import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Badge, Button, Dialog, EmptyState, Field, Input, Section, Select, Spinner, Textarea } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { KeywordAlerts, KeywordCandidates, KeywordList } from '../lib/types';
import { StorePicker, useStoreChoice } from './payments';
import { languageName, storeLanguages } from '../lib/languages';

/**
 * Anahtar kelime havuzu (F5-15): hedeflenen aramalar, Search Console metrikleri, içe alma, kanibalizasyon ve
 * içerik boşluğu uyarıları. Sıralama takibi lisanslı veri sağlayıcı ister ("Kurulum Gerekli").
 */

type Item = KeywordList['items'][number];
const INTENTS = ['informational', 'commercial', 'transactional', 'navigational'] as const;
const TARGET_TONE = { ok: 'moss', missing: 'amber', not_found: 'rose', unverified: 'slate' } as const;

export function KeywordsPage() {
  const t = useT();
  const { stores, storeId, setStoreId } = useStoreChoice();
  return (
    <PageFrame helpKey="page.keywords">
      <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
      {storeId ? (
        <>
          <AlertsSection storeId={storeId} />
          <PoolSection storeId={storeId} languages={storeLanguages(stores.find((s) => s.id === storeId))} />
          <CandidatesSection storeId={storeId} />
          <Section title={t('admin.keywords.rankTitle')} action={<HelpTip k="keywords.rank_tracking" />}>
            <p className="text-sm text-ink-2">
              {t('admin.keywords.rankLead')}{' '}
              <Link to="/entegrasyonlar" className="text-pine underline underline-offset-2">
                {t('admin.nav.integrations')}
              </Link>
            </p>
          </Section>
        </>
      ) : (
        <Spinner label={t('common.loading')} />
      )}
    </PageFrame>
  );
}

function AlertsSection({ storeId }: { storeId: string }) {
  const t = useT();
  const { formatNumber } = useI18n();
  const q = useQuery({
    queryKey: ['keyword-alerts', storeId],
    queryFn: () => api.get<KeywordAlerts>(`/v1/admin/keywords/alerts?storeId=${storeId}`),
  });
  const d = q.data;
  return (
    <Section
      title={t('admin.keywords.alertsTitle')}
      description={t('admin.keywords.alertsLead')}
      action={<HelpTip k="keywords.alerts" />}
    >
      {!d ? (
        <Spinner label={t('common.loading')} />
      ) : !d.cannibalization.length && !d.contentGaps.length ? (
        <p className="text-sm text-ink-2">{t('admin.keywords.noAlerts')}</p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <div>
            <h3 className="mb-2 font-medium">{t('admin.keywords.cannibalTitle')}</h3>
            {d.cannibalization.length === 0 ? (
              <p className="text-sm text-ink-3">{t('admin.keywords.none')}</p>
            ) : (
              <ul className="space-y-2" aria-label={t('admin.keywords.cannibalTitle')}>
                {d.cannibalization.map((c) => (
                  <li key={c.query} className="rounded-md border border-line px-3 py-2 text-sm">
                    <span className="font-medium">{c.query}</span>{' '}
                    {c.keywordId && <Badge tone="slate">{t('admin.keywords.inPool')}</Badge>}
                    <ul className="mt-1 space-y-0.5 text-ink-2">
                      {c.pages.map((p) => (
                        <li key={p.path}>
                          <span className="font-mono text-[0.8125rem]">{p.path}</span>{' '}
                          {t('admin.keywords.pageStats', {
                            impressions: formatNumber(p.impressions),
                            clicks: formatNumber(p.clicks),
                            position: formatNumber(p.position, 1),
                          })}
                          {c.targetPath === p.path && (
                            <>
                              {' '}
                              <Badge tone="moss">{t('admin.keywords.target')}</Badge>
                            </>
                          )}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <h3 className="mb-2 font-medium">{t('admin.keywords.gapTitle')}</h3>
            {d.contentGaps.length === 0 ? (
              <p className="text-sm text-ink-3">{t('admin.keywords.none')}</p>
            ) : (
              <ul className="space-y-1 text-sm" aria-label={t('admin.keywords.gapTitle')}>
                {d.contentGaps.map((g) => (
                  <li key={g.keywordId} className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{g.keyword}</span>
                    <Badge tone={g.reason === 'no_target' ? 'amber' : 'rose'}>
                      {t(`admin.keywords.gapReasons.${g.reason}`)}
                    </Badge>
                    {g.targetPath && (
                      <span className="font-mono text-[0.8125rem] text-ink-3">{g.targetPath}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </Section>
  );
}

function PoolSection({ storeId, languages }: { storeId: string; languages: string[] }) {
  const t = useT();
  const { formatNumber } = useI18n();
  const { can } = usePermissions();
  // Dil (F7-14): mağazanın birden çok dili varsa anahtar kelimeler dile göre süzülür.
  const [lang, setLang] = useState('');
  const [cluster, setCluster] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Item | 'new' | null>(null);
  const q = useQuery({
    queryKey: ['keywords', storeId, lang, cluster, search],
    queryFn: () =>
      api.get<KeywordList>(
        `/v1/admin/keywords?storeId=${storeId}${lang ? `&locale=${lang}` : ''}${
          cluster ? `&cluster=${encodeURIComponent(cluster)}` : ''
        }${search.trim() ? `&q=${encodeURIComponent(search.trim())}` : ''}`,
      ),
  });
  const del = useApiMutation((id: string) => api.del(`/v1/admin/keywords/${id}`), {
    invalidate: [
      ['keywords', storeId],
      ['keyword-alerts', storeId],
      ['keyword-candidates', storeId],
    ],
    success: t('admin.keywords.deleted'),
  });
  const d = q.data;
  return (
    <Section
      title={t('admin.keywords.poolTitle')}
      description={t('admin.keywords.poolLead')}
      action={
        <span className="flex items-center gap-1">
          {can('seo.write') && (
            <Button size="sm" variant="primary" onClick={() => setEditing('new')}>
              {t('admin.keywords.add')}
            </Button>
          )}
          <HelpTip k="keywords.pool" />
        </span>
      }
    >
      <div className={`mb-3 grid gap-3 ${languages.length > 1 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
        {languages.length > 1 && (
          <Field label={t('admin.keywords.language')} help={<HelpTip k="keywords.language" />}>
            {(p) => (
              <Select {...p} value={lang} onChange={(e) => setLang(e.target.value)}>
                <option value="">{t('admin.keywords.allLanguages')}</option>
                {languages.map((l) => (
                  <option key={l} value={l}>
                    {languageName(l)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
        <Field label={t('admin.keywords.cluster')}>
          {(p) => (
            <Select {...p} value={cluster} onChange={(e) => setCluster(e.target.value)}>
              <option value="">{t('admin.keywords.allClusters')}</option>
              {(d?.clusters ?? []).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('admin.keywords.search')}>
          {(p) => <Input {...p} value={search} onChange={(e) => setSearch(e.target.value)} />}
        </Field>
      </div>
      {!d ? (
        <Spinner label={t('common.loading')} />
      ) : d.items.length === 0 ? (
        <EmptyState title={t('admin.keywords.emptyTitle')} description={t('admin.keywords.emptyLead')} />
      ) : (
        <div className="overflow-x-auto rounded-md border border-line">
          <table className="w-full text-sm" aria-label={t('admin.keywords.poolTitle')}>
            <thead className="bg-surface-2 text-ink-2">
              <tr>
                <th className="px-3 py-2 text-start font-medium">{t('admin.keywords.colKeyword')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.keywords.colTarget')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.keywords.colClicks')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.keywords.colImpressions')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.keywords.colPosition')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.keywords.colTopPage')}</th>
                <th className="px-3 py-2 text-end font-medium">
                  <span className="sr-only">{t('common.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {d.items.map((k) => (
                <tr key={k.id} className="border-t border-line align-top">
                  <td className="px-3 py-2">
                    <span className="font-medium">{k.keyword}</span>
                    <span className="mt-0.5 flex flex-wrap gap-1 text-xs text-ink-3">
                      {languages.length > 1 && <Badge tone="slate">{languageName(k.locale)}</Badge>}
                      {k.intent && <span>{t(`admin.keywords.intents.${k.intent}`)}</span>}
                      {k.cluster && <span>· {k.cluster}</span>}
                      {k.cannibalized && <Badge tone="amber">{t('admin.keywords.cannibalized')}</Badge>}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {k.targetPath && <span className="block font-mono text-[0.8125rem]">{k.targetPath}</span>}
                    <Badge tone={TARGET_TONE[k.targetStatus]}>
                      {t(`admin.keywords.targetStatus.${k.targetStatus}`)}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-end tabular-nums">
                    {k.metrics ? formatNumber(k.metrics.clicks) : '—'}
                  </td>
                  <td className="px-3 py-2 text-end tabular-nums">
                    {k.metrics ? formatNumber(k.metrics.impressions) : '—'}
                  </td>
                  <td className="px-3 py-2 text-end tabular-nums">
                    {k.metrics?.position != null ? formatNumber(k.metrics.position, 1) : '—'}
                  </td>
                  <td className="px-3 py-2 font-mono text-[0.8125rem] text-ink-2">
                    {k.topPage?.path ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-end whitespace-nowrap">
                    {can('seo.write') && (
                      <span className="inline-flex gap-1">
                        <Button
                          size="sm"
                          onClick={() => setEditing(k)}
                          aria-label={t('admin.keywords.editItem', { keyword: k.keyword })}
                        >
                          {t('common.edit')}
                        </Button>
                        <ConfirmButton
                          variant="ghost"
                          message={t('admin.keywords.deleteConfirm', { keyword: k.keyword })}
                          onConfirm={() => del.mutateAsync(k.id)}
                        >
                          {t('common.delete')}
                        </ConfirmButton>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-2 text-sm text-ink-3">{t('admin.keywords.metricsNote')}</p>
      {editing && (
        <KeywordDialog
          storeId={storeId}
          languages={languages}
          row={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </Section>
  );
}

function KeywordDialog({
  storeId,
  languages,
  row,
  onClose,
}: {
  storeId: string;
  languages: string[];
  row: Item | null;
  onClose: () => void;
}) {
  const t = useT();
  const [f, setF] = useState({
    locale: row?.locale ?? languages[0] ?? 'tr',
    keyword: row?.keyword ?? '',
    intent: row?.intent ?? '',
    cluster: row?.cluster ?? '',
    targetPath: row?.targetPath ?? '',
    notes: row?.notes ?? '',
  });
  const body = {
    locale: f.locale,
    keyword: f.keyword,
    intent: f.intent || null,
    cluster: f.cluster || null,
    targetPath: f.targetPath || null,
    notes: f.notes || null,
  };
  const save = useApiMutation(
    () =>
      row
        ? api.patch(`/v1/admin/keywords/${row.id}`, { ...body, version: row.version })
        : api.post('/v1/admin/keywords', { ...body, storeId }),
    {
      invalidate: [
        ['keywords', storeId],
        ['keyword-alerts', storeId],
        ['keyword-candidates', storeId],
      ],
      success: t('admin.keywords.saved'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={row ? t('admin.keywords.edit') : t('admin.keywords.add')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(undefined)}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {languages.length > 1 && (
          <Field
            label={t('admin.keywords.language')}
            help={<HelpTip k="keywords.language" />}
            error={fieldError(save.error, 'locale')}
          >
            {(p) => (
              <Select {...p} value={f.locale} onChange={(e) => setF({ ...f, locale: e.target.value })}>
                {languages.map((l) => (
                  <option key={l} value={l}>
                    {languageName(l)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
        <Field label={t('admin.keywords.keyword')} required error={fieldError(save.error, 'keyword')}>
          {(p) => (
            <Input
              {...p}
              value={f.keyword}
              maxLength={200}
              onChange={(e) => setF({ ...f, keyword: e.target.value })}
            />
          )}
        </Field>
        <Field label={t('admin.keywords.intent')} help={<HelpTip k="keywords.intent" />}>
          {(p) => (
            <Select {...p} value={f.intent} onChange={(e) => setF({ ...f, intent: e.target.value })}>
              <option value="">{t('admin.keywords.intentNone')}</option>
              {INTENTS.map((i) => (
                <option key={i} value={i}>
                  {t(`admin.keywords.intents.${i}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field
          label={t('admin.keywords.cluster')}
          optionalLabel={t('common.optional')}
          help={<HelpTip k="keywords.cluster" />}
        >
          {(p) => (
            <Input
              {...p}
              value={f.cluster}
              maxLength={80}
              onChange={(e) => setF({ ...f, cluster: e.target.value })}
            />
          )}
        </Field>
        <Field
          label={t('admin.keywords.targetPath')}
          optionalLabel={t('common.optional')}
          help={<HelpTip k="keywords.target" />}
          error={fieldError(save.error, 'targetPath')}
        >
          {(p) => (
            <Input
              {...p}
              value={f.targetPath}
              placeholder="/urun/omega-3-balik-yagi"
              maxLength={300}
              onChange={(e) => setF({ ...f, targetPath: e.target.value })}
            />
          )}
        </Field>
        <Field label={t('admin.keywords.notes')} optionalLabel={t('common.optional')}>
          {(p) => (
            <Textarea
              {...p}
              rows={2}
              value={f.notes}
              maxLength={1000}
              onChange={(e) => setF({ ...f, notes: e.target.value })}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}

function CandidatesSection({ storeId }: { storeId: string }) {
  const t = useT();
  const { formatNumber } = useI18n();
  const { can } = usePermissions();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [cluster, setCluster] = useState('');
  const q = useQuery({
    queryKey: ['keyword-candidates', storeId],
    queryFn: () => api.get<KeywordCandidates>(`/v1/admin/keywords/candidates?storeId=${storeId}`),
  });
  const imp = useApiMutation(
    () =>
      api.post<{ created: number; skipped: number }>('/v1/admin/keywords/import', {
        storeId,
        queries: [...picked],
        cluster: cluster || null,
      }),
    {
      invalidate: [
        ['keywords', storeId],
        ['keyword-alerts', storeId],
        ['keyword-candidates', storeId],
      ],
      success: (r) => t('admin.keywords.imported', { created: r.created, skipped: r.skipped }),
      onSuccess: () => setPicked(new Set()),
    },
  );
  const d = q.data;
  const toggle = (query: string) =>
    setPicked((s) => {
      const n = new Set(s);
      if (n.has(query)) n.delete(query);
      else n.add(query);
      return n;
    });
  return (
    <Section
      title={t('admin.keywords.candidatesTitle')}
      description={t('admin.keywords.candidatesLead')}
      action={<HelpTip k="keywords.candidates" />}
    >
      {!d ? (
        <Spinner label={t('common.loading')} />
      ) : !d.synced ? (
        <p className="text-sm text-ink-2">
          {t('admin.keywords.notSynced')}{' '}
          <Link to="/arama-performansi" className="text-pine underline underline-offset-2">
            {t('admin.nav.searchConsole')}
          </Link>
        </p>
      ) : d.items.length === 0 ? (
        <p className="text-sm text-ink-2">{t('admin.keywords.noCandidates')}</p>
      ) : (
        <>
          <ul
            className="divide-y divide-line rounded-md border border-line text-sm"
            aria-label={t('admin.keywords.candidatesTitle')}
          >
            {d.items.map((c) => (
              <li key={c.query} className="flex flex-wrap items-center gap-3 px-3 py-2">
                <label className="flex min-w-0 flex-1 items-center gap-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-pine"
                    checked={picked.has(c.query)}
                    disabled={!can('seo.write')}
                    onChange={() => toggle(c.query)}
                  />
                  <span className="font-medium">{c.query}</span>
                </label>
                <span className="text-ink-2 tabular-nums">
                  {t('admin.keywords.candidateStats', {
                    impressions: formatNumber(c.impressions),
                    clicks: formatNumber(c.clicks),
                    position: c.position === null ? '—' : formatNumber(c.position, 1),
                  })}
                </span>
                {c.suggestedPath && (
                  <span className="font-mono text-[0.8125rem] text-ink-3">{c.suggestedPath}</span>
                )}
              </li>
            ))}
          </ul>
          {can('seo.write') && (
            <div className="mt-3 flex flex-wrap items-end gap-3">
              <Field label={t('admin.keywords.importCluster')} optionalLabel={t('common.optional')}>
                {(p) => (
                  <Input {...p} value={cluster} maxLength={80} onChange={(e) => setCluster(e.target.value)} />
                )}
              </Field>
              <Button
                variant="primary"
                disabled={!picked.size}
                loading={imp.isPending}
                onClick={() => imp.mutate(undefined)}
              >
                {t('admin.keywords.importSelected', { n: picked.size })}
              </Button>
            </div>
          )}
        </>
      )}
    </Section>
  );
}
